import React, { useEffect, useState, useMemo } from "react";
import Modal from "../UIElements/Modal";
import { Switch } from "../UIElements/Switch";
import { Icon } from "../UIElements/Icon";
import { NativeSelect } from "../UIElements/Select";
import { Button } from "../UIElements/Button";
import {
  usePromotionPaymethods,
  usePromotionDeliveryZones,
} from "../../../hooks/usePromotions";
import PromotionRulesSchedule, {
  createDefaultPromotionSchedule,
} from "./PromotionRulesSchedule";
import PromotionRulesCategories from "./PromotionRulesCategories";
import PromotionRulesProducts from "./PromotionRulesProducts";
import { ORDER_TYPE_OPTIONS } from "./discountsConstants";

const RuleCard = ({
  active,
  onToggle,
  icon,
  title,
  description,
  children,
  toggle,
  className = "",
}) => {
  const isActive = Boolean(active);

  return (
    <div
      className={`flex flex-col justify-between overflow-hidden rounded-xl border transition-all duration-300 ${
        isActive
          ? "border-primary/50 bg-background shadow-[var(--od-shadow-blue)] shadow-lg"
          : "border-border bg-background/60 hover:border-border"
      } ${className}`}
    >
      <div className="flex items-start gap-3 p-4">
        <Button
          type="button"
          variant="ghost"
          aria-pressed={isActive}
          onClick={onToggle}
          className="focus-visible:ring-ring h-auto min-w-0 flex-1 items-start justify-start gap-3 p-0 text-start whitespace-normal hover:bg-transparent focus-visible:ring-2 focus-visible:outline-none"
        >
          <span
            className={`rounded-lg p-2 transition-colors ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground"
            }`}
          >
            {icon ? <Icon name={icon} size={18} /> : null}
          </span>

          <span className="min-w-0 flex-1">
            <span className="text-foreground mb-1 block truncate text-sm font-semibold">
              {title}
            </span>
            <span className="text-muted-foreground block text-xs leading-snug">
              {description}
            </span>
          </span>
        </Button>
        {toggle ? (
          <div className="shrink-0">
            {React.isValidElement(toggle)
              ? React.cloneElement(toggle, {
                  "aria-label": toggle.props["aria-label"] || title,
                })
              : toggle}
          </div>
        ) : null}
      </div>

      {isActive && children ? (
        <div className="border-border/70 border-t px-4 pt-0 pb-4">
          <div className="pt-3">{children}</div>
        </div>
      ) : null}
    </div>
  );
};

const PromotionRulesModal = ({
  isOpen,
  onClose,
  rules,
  onApplyRules,
  selectedBusinessIds,
  businesses,
  onQuickSave,
}) => {
  const [localRules, setLocalRules] = useState(rules || {});
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const { data: paymethodsData, isLoading: isLoadingPaymethods } =
    usePromotionPaymethods();

  const { data: deliveryZonesData, isLoading: isLoadingDeliveryZones } =
    usePromotionDeliveryZones({
      businessIds: selectedBusinessIds,
      enabled:
        isOpen &&
        Array.isArray(selectedBusinessIds) &&
        selectedBusinessIds.length > 0,
    });

  const paymethods = paymethodsData || [];
  const deliveryZones = deliveryZonesData?.zones || [];

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLocalRules(rules || {});
  }, [isOpen, rules]);

  const handleToggleRule = (key, isEnabled, defaultValue) => {
    setLocalRules((previous) => ({
      ...previous,
      [key]: isEnabled ? (previous[key] ?? defaultValue) : null,
      ...(key === "user_order_count"
        ? {
            user_order_count_condition: isEnabled ? "<" : null,
          }
        : null),
    }));
  };

  const handleChangeRuleValue = (key, value) => {
    setLocalRules((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleToggleFromBoolean = (key) => {
    setLocalRules((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const handleApply = () => {
    onApplyRules(localRules);
    onClose();
  };

  const isRuleEnabled = (key) => {
    const value = localRules?.[key];
    // A rule is considered enabled when it is not null/undefined.
    // Empty strings or empty arrays mean "configured but not filled yet",
    // so they should still be treated as enabled in the UI.
    if (Array.isArray(value)) {
      return value !== null && value !== undefined;
    }
    return value !== null && value !== undefined;
  };

  const selectedOrderTypes = useMemo(
    () => new Set(localRules?.order_types_allowed || []),
    [localRules?.order_types_allowed],
  );

  const selectedPaymethods = useMemo(
    () => new Set(localRules?.paymethods || []),
    [localRules?.paymethods],
  );

  const selectedDeliveryZones = useMemo(
    () => new Set(localRules?.delivery_zones || []),
    [localRules?.delivery_zones],
  );

  const handleToggleArrayItem = (key, id) => {
    setLocalRules((previous) => {
      const current = Array.isArray(previous[key]) ? previous[key] : [];
      const exists = current.includes(id);
      const next = exists
        ? current.filter((item) => item !== id)
        : [...current, id];
      return {
        ...previous,
        [key]: next.length === 0 ? null : next,
      };
    });
  };

  const handleChangeNumber = (key, nextValue) => {
    if (nextValue === "") {
      handleChangeRuleValue(key, "");
      return;
    }

    const numericRegex = /^[0-9]+$/;
    if (!numericRegex.test(nextValue)) {
      return;
    }
    handleChangeRuleValue(key, nextValue);
  };

  const handleChangeMinutes = (key, nextValue) => {
    handleChangeNumber(key, nextValue);
  };

  const handleChangeUserOrderCount = (nextValue) => {
    if (nextValue === "") {
      setLocalRules((previous) => ({
        ...previous,
        user_order_count: "",
        user_order_count_condition: "<",
      }));
      return;
    }

    const numericRegex = /^[0-9]+$/;
    if (!numericRegex.test(nextValue)) {
      return;
    }

    setLocalRules((previous) => ({
      ...previous,
      user_order_count: nextValue,
      user_order_count_condition: "<",
    }));
  };

  const {
    limit,
    limit_per_user,
    max_discount,
    minimum,
    user_order_count,
    valid_from_after_user_last_order_minutes,
    valid_until_after_user_last_order_minutes,
    include_options,
  } = localRules;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Advanced Rules & Conditions"
      subtitle="Define precise logic for when this discount applies."
      maxWidth="max-w-4xl"
      className="ordering-discounts-modal bg-background"
    >
      <div className="space-y-8">
        {/* SCOPE */}
        <section className="space-y-4">
          <h4 className="text-primary flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase">
            <Icon name="ShoppingBag" size={16} className="text-primary" />
            Scope
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RuleCard
              active={isRuleEnabled("products")}
              onToggle={() =>
                handleToggleRule("products", !isRuleEnabled("products"), [])
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("products")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("products", enabled, [])
                  }
                />
              }
              icon="ShoppingCart"
              title="Specific Products"
              description="Limit to specific menu items by their IDs."
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-muted-foreground text-xs">
                  Configure the specific products this promotion applies to.
                </p>
                <Button
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  type="button"
                  onClick={() => setIsProductsModalOpen(true)}
                  className="px-3 py-1.5"
                  aria-label="Open products modal"
                >
                  Configure
                </Button>
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("categories")}
              onToggle={() =>
                handleToggleRule("categories", !isRuleEnabled("categories"), [])
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("categories")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("categories", enabled, [])
                  }
                />
              }
              icon="Layers"
              title="Specific Categories"
              description="Limit to whole categories like “Drinks”."
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-muted-foreground text-xs">
                  Configure which categories are eligible for this promotion.
                </p>
                <Button
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  type="button"
                  onClick={() => setIsCategoriesModalOpen(true)}
                  className="px-3 py-1.5"
                  aria-label="Open categories modal"
                >
                  Configure
                </Button>
              </div>
            </RuleCard>

            <RuleCard
              active={Boolean(include_options)}
              onToggle={() => handleToggleFromBoolean("include_options")}
              toggle={
                <Switch
                  checked={Boolean(include_options)}
                  onCheckedChange={() =>
                    handleToggleFromBoolean("include_options")
                  }
                />
              }
              icon="CheckCircle2"
              title="Include Options"
              description="Apply discount to modifiers & add-ons?"
              className="md:col-span-2"
            >
              <p className="text-muted-foreground text-xs">
                When enabled, the discount applies to the total product price
                including add-ons.
              </p>
            </RuleCard>
          </div>
        </section>

        {/* LIMITS */}
        <section className="space-y-4">
          <h4 className="text-primary flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase">
            <Icon name="Tag" size={16} className="text-primary" />
            Limits
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <RuleCard
              active={isRuleEnabled("limit")}
              onToggle={() =>
                handleToggleRule("limit", !isRuleEnabled("limit"), "")
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("limit")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("limit", enabled, "")
                  }
                />
              }
              icon="Tag"
              title="Max Total Usage"
              description="Total redemptions allowed globally."
            >
              <input
                type="text"
                inputMode="numeric"
                value={limit ?? ""}
                onChange={(event) =>
                  handleChangeNumber("limit", event.target.value)
                }
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                placeholder="e.g. 1000 total redemptions"
                aria-label="Max total usage value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("limit_per_user")}
              onToggle={() =>
                handleToggleRule(
                  "limit_per_user",
                  !isRuleEnabled("limit_per_user"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("limit_per_user")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("limit_per_user", enabled, "")
                  }
                />
              }
              icon="Users"
              title="Max Per User"
              description="Redemptions allowed per customer."
            >
              <input
                type="text"
                inputMode="numeric"
                value={limit_per_user ?? ""}
                onChange={(event) =>
                  handleChangeNumber("limit_per_user", event.target.value)
                }
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                placeholder="e.g. 1 use per customer"
                aria-label="Max per user value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("user_order_count")}
              onToggle={() =>
                handleToggleRule(
                  "user_order_count",
                  !isRuleEnabled("user_order_count"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("user_order_count")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("user_order_count", enabled, "")
                  }
                />
              }
              icon="ShoppingCart"
              title="Order History Limit"
              description="Max previous orders on platform."
            >
              <div className="text-muted-foreground flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">
                  User has less than
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={user_order_count ?? ""}
                  onChange={(event) =>
                    handleChangeUserOrderCount(event.target.value)
                  }
                  className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-16 rounded-md border px-3 py-2 text-center text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                  placeholder="5"
                  aria-label="User has less than order count"
                />
                <span className="text-muted-foreground">orders</span>
              </div>
            </RuleCard>
          </div>
        </section>

        {/* TIME & LOCATION */}
        <section className="space-y-4">
          <h4 className="text-primary flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase">
            <Icon name="Clock" size={16} className="text-primary" />
            Time &amp; Location
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RuleCard
              active={isRuleEnabled("schedule")}
              onToggle={() =>
                handleToggleRule(
                  "schedule",
                  !isRuleEnabled("schedule"),
                  createDefaultPromotionSchedule(),
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("schedule")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule(
                      "schedule",
                      enabled,
                      createDefaultPromotionSchedule(),
                    )
                  }
                />
              }
              icon="Calendar"
              title="Schedule Limit"
              description="Specific days and times."
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-muted-foreground text-xs">
                  Configure the schedule for this promotion.
                </p>
                <Button
                  emphasis="solid"
                  intent="brand"
                  size="touch"
                  type="button"
                  onClick={() => setIsScheduleModalOpen(true)}
                  className="px-3 py-1.5"
                  aria-label="Open schedule modal"
                >
                  Configure
                </Button>
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("delivery_zones")}
              onToggle={() =>
                handleToggleRule(
                  "delivery_zones",
                  !isRuleEnabled("delivery_zones"),
                  [],
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("delivery_zones")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("delivery_zones", enabled, [])
                  }
                />
              }
              icon="MapPin"
              title="Delivery Zones"
              description="Restrict to specific areas."
            >
              <div className="space-y-2">
                {(!Array.isArray(selectedBusinessIds) ||
                  selectedBusinessIds.length === 0) && (
                  <p className="text-muted-foreground text-xs">
                    Select at least one store to load delivery zones.
                  </p>
                )}
                {isLoadingDeliveryZones && (
                  <p className="text-muted-foreground text-xs">
                    Loading delivery zones...
                  </p>
                )}
                {!isLoadingDeliveryZones && deliveryZones.length === 0 && (
                  <p className="text-muted-foreground text-xs">
                    No delivery zones available.
                  </p>
                )}

                {!isLoadingDeliveryZones && deliveryZones.length > 0 ? (
                  <div className="relative">
                    <NativeSelect
                      value={Array.from(selectedDeliveryZones)[0] ?? ""}
                      onChange={(event) => {
                        const nextId = Number(event.target.value);
                        if (!Number.isFinite(nextId)) {
                          handleChangeRuleValue("delivery_zones", null);
                          return;
                        }
                        handleChangeRuleValue("delivery_zones", [nextId]);
                      }}
                      className="border-border bg-background focus:ring-ring text-foreground w-full cursor-pointer appearance-none rounded-lg border py-2.5 pr-10 pl-4 transition-all outline-none focus:border-transparent focus:ring-2"
                      aria-label="Select delivery zone"
                    >
                      <option value="">Select delivery zone</option>
                      {deliveryZones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name} ({zone.minimum} - {zone.price})
                        </option>
                      ))}
                    </NativeSelect>
                    <Icon
                      name="ChevronDown"
                      size={16}
                      className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                ) : null}
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("valid_from_after_user_last_order_minutes")}
              onToggle={() =>
                handleToggleRule(
                  "valid_from_after_user_last_order_minutes",
                  !isRuleEnabled("valid_from_after_user_last_order_minutes"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled(
                    "valid_from_after_user_last_order_minutes",
                  )}
                  onCheckedChange={(enabled) =>
                    handleToggleRule(
                      "valid_from_after_user_last_order_minutes",
                      enabled,
                      "",
                    )
                  }
                />
              }
              icon="Clock"
              title="Minutes From Last Order"
              description="Minimum cooldown time."
            >
              <input
                type="text"
                inputMode="numeric"
                value={valid_from_after_user_last_order_minutes ?? ""}
                onChange={(event) =>
                  handleChangeMinutes(
                    "valid_from_after_user_last_order_minutes",
                    event.target.value,
                  )
                }
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                placeholder="e.g. 60 minutes"
                aria-label="Minutes from last order value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled(
                "valid_until_after_user_last_order_minutes",
              )}
              onToggle={() =>
                handleToggleRule(
                  "valid_until_after_user_last_order_minutes",
                  !isRuleEnabled("valid_until_after_user_last_order_minutes"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled(
                    "valid_until_after_user_last_order_minutes",
                  )}
                  onCheckedChange={(enabled) =>
                    handleToggleRule(
                      "valid_until_after_user_last_order_minutes",
                      enabled,
                      "",
                    )
                  }
                />
              }
              icon="Clock"
              title="Minutes Until Last Order"
              description="Maximum inactivity time."
            >
              <input
                type="text"
                inputMode="numeric"
                value={valid_until_after_user_last_order_minutes ?? ""}
                onChange={(event) =>
                  handleChangeMinutes(
                    "valid_until_after_user_last_order_minutes",
                    event.target.value,
                  )
                }
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                placeholder="e.g. 1440 minutes"
                aria-label="Minutes until last order value"
              />
            </RuleCard>
          </div>
        </section>

        {/* CART & PAYMENT */}
        <section className="space-y-4">
          <h4 className="text-success flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase">
            <Icon name="DollarSign" size={16} className="text-success" />
            Cart &amp; Payment
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RuleCard
              active={isRuleEnabled("minimum")}
              onToggle={() =>
                handleToggleRule("minimum", !isRuleEnabled("minimum"), "")
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("minimum")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("minimum", enabled, "")
                  }
                />
              }
              icon="ShoppingCart"
              title="Minimum Purchase"
              description="Required cart subtotal."
            >
              <input
                type="text"
                inputMode="numeric"
                value={minimum ?? ""}
                onChange={(event) =>
                  handleChangeNumber("minimum", event.target.value)
                }
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                placeholder="e.g. 20 (currency units)"
                aria-label="Minimum purchase value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("max_discount")}
              onToggle={() =>
                handleToggleRule(
                  "max_discount",
                  !isRuleEnabled("max_discount"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("max_discount")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("max_discount", enabled, "")
                  }
                />
              }
              icon="AlertCircle"
              title="Max Discount Limit"
              description="Cap the discount amount."
            >
              <input
                type="text"
                inputMode="numeric"
                value={max_discount ?? ""}
                onChange={(event) =>
                  handleChangeNumber("max_discount", event.target.value)
                }
                className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] focus:border-transparent focus:ring-2 focus:outline-none"
                placeholder="e.g. 20 (USD)"
                aria-label="Max discount limit value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("paymethods")}
              onToggle={() =>
                handleToggleRule("paymethods", !isRuleEnabled("paymethods"), [])
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("paymethods")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("paymethods", enabled, [])
                  }
                />
              }
              icon="CreditCard"
              title="Payment Methods"
              description="Allowed payment types."
            >
              <div className="border-border bg-background/60 flex max-h-[118px] flex-wrap gap-2 overflow-y-auto rounded-md border p-2">
                {isLoadingPaymethods && (
                  <p className="text-muted-foreground text-xs">
                    Loading payment methods...
                  </p>
                )}
                {!isLoadingPaymethods && paymethods.length === 0 && (
                  <p className="text-muted-foreground text-xs">
                    No payment methods available.
                  </p>
                )}
                {!isLoadingPaymethods &&
                  paymethods.map((paymethod) => (
                    <label
                      key={paymethod.id}
                      className={`inline-flex max-w-full cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] font-medium transition-colors select-none ${
                        selectedPaymethods.has(paymethod.id)
                          ? "border-primary/40 bg-accent/15 text-white"
                          : "border-border bg-background/40 text-foreground hover:bg-background/70"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="border-border bg-background h-4 w-4 rounded accent-blue-500"
                        checked={selectedPaymethods.has(paymethod.id)}
                        onChange={() =>
                          handleToggleArrayItem("paymethods", paymethod.id)
                        }
                        aria-label={`Toggle paymethod ${
                          paymethod.name ||
                          paymethod.gateway ||
                          "payment method"
                        }`}
                      />
                      <span className="max-w-[160px] truncate">
                        {paymethod.name || paymethod.gateway}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selectedPaymethods.has(paymethod.id)}
                        onChange={() =>
                          handleToggleArrayItem("paymethods", paymethod.id)
                        }
                      />
                    </label>
                  ))}
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("order_types_allowed")}
              onToggle={() =>
                handleToggleRule(
                  "order_types_allowed",
                  !isRuleEnabled("order_types_allowed"),
                  [],
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("order_types_allowed")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("order_types_allowed", enabled, [])
                  }
                />
              }
              icon="Truck"
              title="Order Type"
              description="Delivery, Pickup, etc."
            >
              <div className="grid grid-cols-2 gap-2">
                {ORDER_TYPE_OPTIONS.map((typeOption) => (
                  <button
                    key={typeOption.value}
                    type="button"
                    onClick={() =>
                      handleToggleArrayItem(
                        "order_types_allowed",
                        typeOption.value,
                      )
                    }
                    className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-[length:var(--font-size-product-ui)] font-medium transition-colors ${
                      selectedOrderTypes.has(typeOption.value)
                        ? "border-primary/60 bg-accent/20 text-primary"
                        : "border-border bg-background text-muted-foreground hover:bg-card"
                    }`}
                    aria-label={`Toggle order type ${typeOption.label}`}
                  >
                    {typeOption.label}
                  </button>
                ))}
              </div>
            </RuleCard>
          </div>
        </section>

        <div className="border-border flex items-center justify-between border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-[length:var(--font-size-product-ui)] transition-colors"
          >
            Close
          </button>
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            type="button"
            onClick={handleApply}
            className="px-5 py-2"
          >
            Save Rules
          </Button>
        </div>
      </div>
      {/* Products rule modal (design-only for now) */}
      <Modal
        isOpen={isProductsModalOpen}
        onClose={() => setIsProductsModalOpen(false)}
        title="Products rule"
        subtitle="Select products to include or exclude for this promotion."
        maxWidth="max-w-xl"
        className="ordering-discounts-modal bg-background"
      >
        <div className="space-y-4">
          <PromotionRulesProducts
            selectedBusinessIds={selectedBusinessIds}
            businesses={businesses}
            value={localRules?.products}
            onChange={(nextValue) => {
              handleChangeRuleValue("products", nextValue);
            }}
          />

          <div className="border-border flex items-center justify-end gap-2 border-t pt-2">
            <button
              type="button"
              onClick={() => {
                if (onQuickSave) {
                  onQuickSave({ products: localRules?.products ?? null });
                }
                setIsProductsModalOpen(false);
              }}
              className="text-muted-foreground hover:bg-card hover:text-foreground rounded-lg px-4 py-2 text-[length:var(--font-size-product-ui)] font-semibold transition-colors"
              aria-label="Close products modal"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Categories rule modal (design-only for now) */}
      <Modal
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        title="Categories rule"
        subtitle="Select categories to include or exclude for this promotion."
        maxWidth="max-w-xl"
        className="ordering-discounts-modal bg-background"
      >
        <div className="space-y-4">
          <PromotionRulesCategories
            selectedBusinessIds={selectedBusinessIds}
            businesses={businesses}
            value={localRules?.categories}
            onChange={(nextValue) => {
              handleChangeRuleValue("categories", nextValue);
            }}
          />

          <div className="border-border flex items-center justify-end gap-2 border-t pt-2">
            <button
              type="button"
              onClick={() => {
                if (onQuickSave) {
                  onQuickSave({ categories: localRules?.categories ?? null });
                }
                setIsCategoriesModalOpen(false);
              }}
              className="text-muted-foreground hover:bg-card hover:text-foreground rounded-lg px-4 py-2 text-[length:var(--font-size-product-ui)] font-semibold transition-colors"
              aria-label="Close categories modal"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Schedule rule modal (design-only for now) */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        title="Schedule rule"
        subtitle="Configure the schedule for when this promotion is active."
        maxWidth="max-w-xl"
        className="ordering-discounts-modal bg-background"
      >
        <div className="space-y-4">
          <PromotionRulesSchedule
            schedule={
              Array.isArray(localRules?.schedule) ? localRules.schedule : []
            }
            onScheduleChange={(nextSchedule) => {
              handleChangeRuleValue("schedule", nextSchedule);
            }}
          />

          <div className="border-border flex items-center justify-end gap-2 border-t pt-2">
            <button
              type="button"
              onClick={() => setIsScheduleModalOpen(false)}
              className="text-muted-foreground hover:bg-card hover:text-foreground rounded-lg px-4 py-2 text-[length:var(--font-size-product-ui)] font-semibold transition-colors"
              aria-label="Close schedule modal"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default PromotionRulesModal;
